import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { api } from '@/api/client';
import { speakText, stopSpeaking, VOICE_MODE_INSTRUCTIONS } from './voice';
import { ResponseBody } from './ResponseBody';
import { ExecutionPanel } from './ExecutionPanel';
import { SectionRail } from './SectionRail';
import { renderMermaidIn } from './mermaid-render';
import { renderSvgIn } from './svg-render';
import { VisionResult, extractVisionTaskId } from './VisionResult';
import { useRemoteResolveCard, CardBoundary, type ToolResultSidecar } from './remote-cards';
import { ProductPickCard, PRODUCT_PICK_ACTION } from './ProductPickCard';
import { DeepSearchCard, DEEP_SEARCH_ACTION } from './DeepSearchCard';
import { SourceCards } from './SourceCards';
import { StoriesBriefing } from './StoriesBriefing';
import { ThinkingStatus, ImageGenStatus } from './ThinkingStatus';
import { BrewLoader } from './BrewLoader';

// Raw SVG for the compact-list copy button's two states — it lives in markdown
// HTML (dangerouslySetInnerHTML), so the delegated handler swaps innerHTML
// rather than rendering a React glyph. COPY_ICON mirrors markdown.ts's emitted
// glyph so the restore-after-copy markup matches byte-for-byte.
const COPY_ICON =
  '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
  '<rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" stroke-width="1.8"/>' +
  '<path d="M5 15V5a2 2 0 0 1 2-2h8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>' +
  '</svg>';
const CHECK_ICON =
  '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
  '<path d="M20 6 9 17l-5-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
  '</svg>';

/** Mint a short-lived, path-scoped preview CAPABILITY for a workspace file. The
 *  account JWT is sent in the Authorization header (via the api client) and NEVER
 *  put in the resulting preview/download URL — a preview renders model-generated
 *  HTML that can read its own `window.location`, so a JWT there is exfiltratable.
 *  The capability is scoped to one file + expires in ~2 min. See docs §8.3. */
async function mintPreviewToken(filePath: string): Promise<string | null> {
  try {
    const r = await api.get<{ token: string }>(`/api/preview-grant?path=${encodeURIComponent(filePath)}`);
    return r?.token ?? null;
  } catch {
    return null;
  }
}

/** Build a /api/preview URL from a capability token (never the account JWT).
 *  `download=1` streams the raw file as an attachment; without it the endpoint
 *  renders an inline (sandboxed) preview. */
function previewUrl(filePath: string, capability: string, download = false): string {
  const q = new URLSearchParams({ path: filePath });
  if (download) q.set('download', '1');
  q.set('pt', capability);
  return `/api/preview?${q.toString()}`;
}

/** Mint a capability, then trigger a download — without the JWT ever touching a URL. */
async function downloadFile(filePath: string, name: string): Promise<void> {
  const cap = await mintPreviewToken(filePath);
  if (!cap) return;
  const a = document.createElement('a');
  a.href = previewUrl(filePath, cap, true);
  a.download = name;
  a.rel = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** Save the lightbox image. Generated images arrive as data: URLs (or same-origin
 *  blobs), so fetch→blob→object-URL gives a real file save with the right
 *  extension. If a cross-origin URL blocks fetch, fall back to opening it. */
async function downloadImageSrc(src: string): Promise<void> {
  try {
    const res = await fetch(src);
    const blob = await res.blob();
    const ext = (blob.type.split('/')[1] || 'png').replace('+xml', '').replace('jpeg', 'jpg');
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `breww-image.${ext}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  } catch {
    window.open(src, '_blank', 'noopener,noreferrer');
  }
}

/** Can this generated file be VIEWED in the side PreviewPane? It handles HTML,
 *  PDFs, the Office formats the server converts, images, and text — all at full
 *  size in the resizable pane. HTML included: a model-built page is best seen big
 *  in the pane (open/new-tab/download right there), not cramped inline in chat. */
function canViewInPane(name: string): boolean {
  return /\.(html?|pdf|png|jpe?g|gif|webp|svg|txt|md|json|csv|docx?|xlsx?|pptx?|odt|ods|odp|rtf)$/i.test(name.trim());
}


/** Assistant prose. The rendering itself lives in the shared ResponseBody
 *  component (throttled re-parse + streaming tail stability), so this file and
 *  every other answer surface draw markdown identically. */
function MessageBody({ body, streaming }: { body: string; streaming: boolean }): JSX.Element {
  return <ResponseBody body={body} streaming={streaming} />;
}

type FileKind = 'pdf' | 'sheet' | 'doc' | 'slides' | 'image' | 'code' | 'text';

/** Display metadata for a download card: category label, extension, icon kind,
 *  and an accent color keyed off the file type (Claude-style file cards). */
/** Spec artifacts (*.deck.json / *.doc.json / *.sheet.json / *.pdf.json) are the
 *  INTERNAL intermediate the agent writes; Brew renders them into the premium
 *  binary and DELETES the spec. They must never appear as a deliverable card — the
 *  card would 404 the moment the server removes the spec, and it's noise next to
 *  the real .pptx/.docx/.xlsx. */
function isSpecArtifact(name: string): boolean {
  return /\.(deck|doc|sheet|pdf)\.json$/i.test(name.trim());
}

function fileMeta(name: string): { ext: string; category: string; kind: FileKind; accent: string } {
  const ext = (name.split('.').pop() || '').toLowerCase();
  const table: Record<string, { category: string; kind: FileKind; accent: string }> = {
    pdf: { category: 'Document', kind: 'pdf', accent: '#e5484d' },
    xlsx: { category: 'Spreadsheet', kind: 'sheet', accent: '#1f9d55' },
    xls: { category: 'Spreadsheet', kind: 'sheet', accent: '#1f9d55' },
    csv: { category: 'Spreadsheet', kind: 'sheet', accent: '#1f9d55' },
    docx: { category: 'Document', kind: 'doc', accent: '#2b6cb0' },
    doc: { category: 'Document', kind: 'doc', accent: '#2b6cb0' },
    pptx: { category: 'Presentation', kind: 'slides', accent: '#dd6b20' },
    ppt: { category: 'Presentation', kind: 'slides', accent: '#dd6b20' },
    md: { category: 'Document', kind: 'text', accent: '#6b7280' },
    txt: { category: 'Text', kind: 'text', accent: '#6b7280' },
    html: { category: 'Web page', kind: 'code', accent: '#dd6b20' },
    json: { category: 'Data', kind: 'code', accent: '#8b5cf6' },
    png: { category: 'Image', kind: 'image', accent: '#7c3aed' },
    jpg: { category: 'Image', kind: 'image', accent: '#7c3aed' },
    jpeg: { category: 'Image', kind: 'image', accent: '#7c3aed' },
    webp: { category: 'Image', kind: 'image', accent: '#7c3aed' },
    gif: { category: 'Image', kind: 'image', accent: '#7c3aed' },
  };
  const m = table[ext];
  return {
    ext: ext || 'file',
    category: m?.category ?? 'File',
    kind: m?.kind ?? 'text',
    accent: m?.accent ?? '#6b7280',
  };
}

/**
 * The reasoning-model Thinking block — the model's REAL chain-of-thought, not a
 * canned status line. Streams live in an expanded panel while the model reasons;
 * the moment the answer's first token arrives (`sec` gets stamped) it collapses
 * to a "Thought for Ns ▸" summary the reader can click back open. Claude/DeepSeek/
 * o1 behaviour. Only ever rendered when a reasoning model actually produced
 * thinking — nothing is fabricated for ordinary models.
 */
function ThinkingTrail({ text, sec }: { text: string; sec?: number | undefined }): JSX.Element {
  const done = sec != null;
  const [open, setOpen] = useState(!done);   // open while thinking, collapsed once done
  // Auto-collapse exactly once, when reasoning finishes — after that the reader
  // owns the toggle (re-opening won't be undone).
  useEffect(() => { if (done) setOpen(false); }, [done]);

  return (
    <div className={`think${open ? ' is-open' : ''}`}>
      <button type="button" className="think__toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true"
             style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .12s' }}>
          <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="think__label">
          {done ? `Thought for ${sec}s` : 'Thinking'}
          {!done && <span className="think__dots" aria-hidden="true"><span>.</span><span>.</span><span>.</span></span>}
        </span>
      </button>
      {open && <div className="think__body">{text}</div>}
    </div>
  );
}

/** Pencil glyph for the "edit last prompt" affordance. */
function EditGlyph() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 20h4L18.5 9.5a2.12 2.12 0 0 0-3-3L5 17v3Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
      <path d="M13.5 6.5l4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

/** Two-page glyph for the copy affordance; check glyph for the copied state. */
function CopyGlyph({ done }: { done: boolean }) {
  return done ? (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M20 6 9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ) : (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="1.8" />
      <path d="M5 15V5a2 2 0 0 1 2-2h8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * Quiet copy-to-clipboard icon button for a whole message (prompt or reply).
 * Distinct from the per-code-block `.code-copy` (which lives in markdown HTML
 * and is wired by a delegated handler) — this is a real React control with its
 * own copied-state, so it flips to a check for ~1.6s on success. `variant`
 * picks the placement styling: 'user' hides until the row is hovered, while
 * 'assistant' sits always-visible in an actions row under the reply.
 */
function CopyButton({ text, variant, title }: { text: string; variant: 'user' | 'assistant'; title: string }) {
  const [copied, setCopied] = useState(false);
  const onClick = () => {
    if (!text || !navigator.clipboard) return;
    navigator.clipboard.writeText(text).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1600);
      },
      () => {},
    );
  };
  return (
    <button
      type="button"
      className={`msg-copy msg-copy--${variant}${copied ? ' is-copied' : ''}`}
      onClick={onClick}
      aria-label={title}
      title={copied ? 'Copied' : title}
    >
      <CopyGlyph done={copied} />
    </button>
  );
}

/** Circular-arrow glyph for "run this turn again". */
function RegenGlyph() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M20 12a8 8 0 1 1-2.34-5.66" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M20 4v5h-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * "Regenerate" — re-run the most recent turn with the same prompt. Deliberately
 * offered ONLY on the newest reply: the server-side rerun truncates the LAST
 * user turn, so exposing it on an older message would silently regenerate a
 * different exchange than the one clicked. Shares CopyButton's styling so the
 * actions row reads as one set of quiet controls.
 */
function RegenerateButton({ onClick, title }: { onClick: () => void; title: string }) {
  return (
    <button
      type="button"
      className="msg-copy msg-copy--assistant"
      onClick={onClick}
      aria-label={title}
      title={title}
    >
      <RegenGlyph />
    </button>
  );
}

/** ▶ / ❚❚ glyph for the Play-voice control. */
function PlayGlyph({ playing }: { playing: boolean }) {
  return playing ? (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="6" y="5" width="4" height="14" rx="1" fill="currentColor" />
      <rect x="14" y="5" width="4" height="14" rx="1" fill="currentColor" />
    </svg>
  ) : (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M8 5v14l11-7-11-7z" fill="currentColor" />
    </svg>
  );
}

/**
 * "Play voice" — synthesise THIS reply to speech on demand (OpenAI TTS via
 * POST /api/tts) and play it. Nothing ever auto-plays: speech is generated only
 * when the user clicks (cost guard, VOICE_AI_DESIGN §5). Click again to stop.
 * Shares CopyButton's quiet styling so the actions row reads as one control set.
 */
function PlayVoiceButton({ text }: { text: string }) {
  const [state, setState] = useState<'idle' | 'loading' | 'playing'>('idle');
  const audioRef = useRef<HTMLAudioElement | null>(null);
  useEffect(() => () => {
    // Stop this button's audio if it's still playing when the message unmounts.
    if (audioRef.current) { stopSpeaking(); audioRef.current = null; }
  }, []);

  const onClick = async (): Promise<void> => {
    if (state !== 'idle') { stopSpeaking(); audioRef.current = null; setState('idle'); return; }
    if (!text.trim()) return;
    setState('loading');
    try {
      const audio = await speakText(text, () => {
        if (audioRef.current === audio) audioRef.current = null;
        setState('idle');
      }, undefined, VOICE_MODE_INSTRUCTIONS); // A8: warm delivery parity with live mode
      audioRef.current = audio;
      setState('playing');
    } catch {
      setState('idle');
    }
  };

  return (
    <button
      type="button"
      className={`msg-copy msg-copy--assistant${state !== 'idle' ? ' is-copied' : ''}`}
      onClick={() => void onClick()}
      aria-label="Play voice"
      title={state === 'loading' ? 'Generating voice…' : state === 'playing' ? 'Stop' : 'Play voice'}
      disabled={state === 'loading'}
    >
      <PlayGlyph playing={state === 'playing'} />
    </button>
  );
}

/** A rounded document glyph with a type-specific mark, tinted by --file-accent. */
function FileGlyph({ kind }: { kind: FileKind }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {/* page */}
      <path
        d="M13.5 2.5H7A2.5 2.5 0 0 0 4.5 5v14A2.5 2.5 0 0 0 7 21.5h10a2.5 2.5 0 0 0 2.5-2.5V8.5l-6-6Z"
        fill="currentColor" fillOpacity="0.14" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"
      />
      <path d="M13.5 2.5V8.5h6" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      {/* type mark */}
      {kind === 'sheet' ? (
        <path d="M8 12h8M8 15h8M11 12v6M15 12v6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      ) : kind === 'slides' ? (
        <rect x="8" y="12.5" width="8" height="5" rx="1" stroke="currentColor" strokeWidth="1.3" />
      ) : kind === 'image' ? (
        <>
          <circle cx="10" cy="13.5" r="1.1" fill="currentColor" />
          <path d="M8 18l2.4-2.4L13 18l1.6-1.6L16 18" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </>
      ) : kind === 'code' ? (
        <path d="M10.5 12.5 8.5 15l2 2.5M13.5 12.5 15.5 15l-2 2.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
      ) : (
        <path d="M8 13h8M8 15.5h8M8 18h5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      )}
    </svg>
  );
}

/**
 * Split a trailing "You might also ask:" section off an assistant reply → the
 * body without it plus the follow-up questions, so they render as clickable
 * chips instead of static text. Works for every module.
 */
function extractFollowups(text: string): { body: string; followups: string[] } {
  if (!text) return { body: text, followups: [] };
  const m = text.match(/(?:^|\n)\s*(?:[*_]{0,2})\s*you might also ask\s*:?\s*(?:[*_]{0,2})\s*\n/i);
  if (!m || m.index == null) return { body: text, followups: [] };
  const body = text.slice(0, m.index).replace(/\s+$/, '');
  const rest = text.slice(m.index + m[0].length);
  const followups = rest
    .split('\n')
    .map((l) => l.replace(/^\s*(?:[-*•]|\d+[.)])\s*/, '').replace(/^[*_`\s]+|[*_`\s]+$/g, '').trim())
    .filter((l) => l.length > 1 && l.length < 240)
    .slice(0, 6);
  return { body, followups };
}

/** The "why this mode" badge (Breww Pro brief §2/§3): make Auto's routing choice
 *  visible so users learn the modes by watching. Fast/Auto turns stay clean (no
 *  badge); Pro / Image / a persona-folded-under-Pro turn shows its mode. Kept
 *  local so Messages doesn't import from Chat (which imports Messages). */
const MODE_BADGES: Record<string, { icon: string; label: string }> = {
  'brew-pro': { icon: '✦', label: 'Pro' },
  'brew-imagen': { icon: '🎨', label: 'Image' },
};
function modeBadgeFor(wrapper?: string): { icon: string; label: string } | null {
  if (!wrapper) return null;
  if (MODE_BADGES[wrapper]) return MODE_BADGES[wrapper]!;
  // Default (Auto) and Fast stay unbadged; any other wrapper is a
  // persona/product/solution that folds under Pro.
  if (wrapper === 'brew-generic-0.5' || wrapper === 'brew-fast') return null;
  return { icon: '✦', label: 'Pro' };
}

/** Quick "refine in place" chips shown under a built file (Breww Pro brief §4).
 *  Each sends a follow-up that the sticky-builder path keeps on the agent, so the
 *  SAME deliverable is iterated rather than dropping to plain chat. Chips are
 *  tailored to the file kind. */
function refineChipsFor(name: string): Array<{ label: string; prompt: string }> {
  const ext = (name.split('.').pop() || '').toLowerCase();
  if (ext === 'pdf' || ext === 'docx' || ext === 'doc') {
    return [
      { label: 'Make it longer', prompt: 'Make it longer and more detailed.' },
      { label: 'Add a summary', prompt: 'Add a one-paragraph summary at the top.' },
      ext === 'pdf'
        ? { label: 'Export as DOCX', prompt: 'Give me the same document as an editable DOCX.' }
        : { label: 'Export as PDF', prompt: 'Give me the same document as a PDF.' },
    ];
  }
  if (ext === 'pptx' || ext === 'ppt') {
    return [
      { label: 'Add a slide', prompt: 'Add a slide covering the key takeaways.' },
      { label: 'Tighter slides', prompt: 'Condense it into fewer, tighter slides.' },
      { label: 'Speaker notes', prompt: 'Add speaker notes to each slide.' },
    ];
  }
  if (ext === 'xlsx' || ext === 'xls' || ext === 'csv') {
    return [
      { label: 'Add a chart', prompt: 'Add a chart visualising this data.' },
      { label: 'Add a totals row', prompt: 'Add a totals row.' },
      { label: 'Export as PDF', prompt: 'Give me this as a PDF too.' },
    ];
  }
  return [
    { label: 'Make it longer', prompt: 'Make it longer and more detailed.' },
    { label: 'Redo it', prompt: 'Redo it with a fresh take.' },
  ];
}

/**
 * Strip the agent's raw "sandbox:" download link out of a reply. The file is
 * offered as a proper download card below, so the local sandbox path is noise —
 * remove the markdown link and any now-dangling "download it below" sentence.
 */
function stripSandboxLinks(text: string): string {
  if (!text) return text;
  return text
    // [Download foo.pdf](sandbox:/…/foo.pdf) — with or without a leading label.
    .replace(/[^\n]*\[[^\]]*\]\(sandbox:[^)]*\)[^\n]*/gi, '')
    // A leftover "You can download it using the link below:" pointing at nothing.
    .replace(/[^\n]*\bdownload it (?:using|from)[^\n]*:?\s*(?=\n|$)/gi, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Pull the "Recommendation:" line out of a reply (shown even under a card). */
function extractRecommendation(text: string): string | null {
  if (!text) return null;
  const m = text.match(/(?:^|\n)\s*(?:[*_]{0,2})\s*recommendation\s*(?:[*_]{0,2})\s*:\s*([\s\S]*?)(?=\n\s*(?:[*_]{0,2})\s*you might also ask|\n\s*\n|$)/i);
  if (!m || !m[1]) return null;
  const v = m[1].replace(/[*_`]+/g, '').replace(/\s+/g, ' ').trim();
  return v.length > 1 ? v : null;
}

/** A "While you're here" proactive card (Breww Pro brief §6) — a typed next-step
 *  suggestion generated fire-and-forget from the turn's Q+A by POST /api/assist. */
export type AssistCardType = 'deliverable' | 'quick_action' | 'personalise' | 'explore' | 'media';
export interface AssistCard { type: AssistCardType; label: string; prompt: string; }

/** Mission OS (Universal Agent Blueprint) — a broad ask is a MISSION, planned by
 *  POST /api/mission and rendered in the Agent Studio: a goal, the dedicated
 *  agents, real deliverables, and EXECUTABLE actions (a click runs a capability). */
export type MissionActionCategory = 'complete' | 'improve' | 'act' | 'explore' | 'personalise' | 'monitor' | 'share';
export interface MissionAgent { role: string; task: string; output?: string; grounded?: boolean; }
export interface MissionDeliverable { label: string; format: string; summary?: string; prompt: string; premium?: boolean; }
export interface MissionAction { category: MissionActionCategory; label: string; prompt: string; uses: string; produces: string; duration?: string; cost?: string; approval: boolean; premium?: boolean; }
/** A real live web source fetched for the mission (searxng→tavily). Proof the crew
 *  did real work — a clickable URL, not a planner-invented line. */
export interface MissionSource { title: string; url: string; domain: string; }
/** A real reference image fetched for the mission (Tavily include_images) — the
 *  visual gallery the user can browse, expand and minimise. */
export interface MissionImage { url: string; alt: string; }
export interface MissionPlan {
  missionType: string;
  goal: string;
  insight?: string;
  agents: MissionAgent[];
  deliverables: MissionDeliverable[];
  actions: MissionAction[];
  /** Real live sources fetched for this mission (empty if not a live mission or
   *  search unconfigured/failed — see `live`). */
  sources?: MissionSource[];
  /** Real reference images for the visual gallery (empty if none / unconfigured). */
  images?: MissionImage[];
  /** Honest status of the live fetch: which provider served + how many sources +
   *  whether a fetch was actually attempted (count 0 + attempted = "searched,
   *  nothing fresh"; provider 'unconfigured'/'error' = "couldn't fetch live"). */
  live?: { provider: string; count: number; attempted?: boolean };
  /** Caller's subscription tier — drives premium lock rendering. */
  tier?: string;
}

export interface Msg {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  wrapper?: string;
  /** Model the bridge REQUESTED for this turn (a build-override or house-skill
   *  pin). Shown as a small debug tag. Null/undefined = Hermes' configured
   *  default (the true post-router model for agentic turns is chosen inside brew
   *  and not visible to the client). */
  model?: string | null;
  /** Proactive "While you're here" cards for THIS assistant turn. undefined =
   *  not computed yet; [] = computed, nothing useful. Drives the right-side rail. */
  assistCards?: AssistCard[];
  /** Mission OS plan for THIS turn — drives the Agent Studio. undefined = not
   *  computed; null = computed, this was a plain answer (no mission). */
  missionPlan?: MissionPlan | null;
  /** The user prompt that produced this mission — the query the studio uses to
   *  progressively enrich (live sources + images) and to "gather more". */
  missionQuery?: string;
  /** This turn's prompt carried clear mission intent, so the studio was already
   *  promised (skeleton shown). Keeps the panel for any plan with real work in it
   *  even when the planner mislabels it 'answer' — see missionShowable in Chat.tsx. */
  missionCommitted?: boolean;
  /** This turn's generation FAILED — `content` is the error, not an answer.
   *  Persisted (so a reload still shows what went wrong instead of leaving the
   *  question apparently unanswered) and rendered as an error rather than as
   *  something the assistant said. */
  error?: boolean;
  /** A reasoning model's real chain-of-thought (delta.reasoning), accumulated
   *  live. Rendered in a collapsible Thinking block ABOVE the answer — never in
   *  `content`. Absent for non-reasoning models (nothing fabricated). */
  thinking?: string;
  /** Seconds the model spent thinking, stamped when the answer's first token
   *  arrives. Its presence means reasoning is DONE → collapse to "Thought for Ns". */
  thinkingSec?: number;
  /** Data-URL thumbnail of an attached image — rendered as the bubble itself
   *  (the picture) instead of the "📎 filename" text. */
  imageUrl?: string;
  /** Structured tool-result sidecars forwarded by the engine (delta.tool_results).
   *  When an action has a registered native card, it renders in place of the
   *  markdown reply — the same payload the brew-mfe iframe consumed, rendered
   *  natively so the iframe isn't needed. */
  toolResults?: ToolResultSidecar[];
  /** Files the agentic engine created/modified this turn (Agent mode). Rendered
   *  as download chips under the reply — each links to /api/preview?path=…. */
  files?: AgenticFile[];
  /** Files the USER attached to this turn (PDF/Word/MD/image). Rendered as chips
   *  on the user bubble; the agent reads them from the workspace. */
  attachments?: AttachedFile[];
  /** What the engine is doing RIGHT NOW, from its `brew.progress` events
   *  ("Creating your image…", "Searching 3 queries"). Replaces the generic
   *  rotating "Thinking…" while a turn is still working, so a long silence
   *  (image generation, a Deep Search pass) says what it's waiting on rather
   *  than looking identical to a slow text reply. Cleared when the turn ends. */
  progress?: string;
  /** The `phase` of that progress event ('image', 'research', 'tool_call'…).
   *  Lets the bubble pick a shape for the wait: an image turn gets a picture
   *  frame, everything else gets a line of text. */
  progressPhase?: string;
  /** Structured web_search sources (Perplexity pattern) — sent by the backend as
   *  a `brew.sources` event, separate from the streamed answer. Rendered as source
   *  cards under the reply; the frontend owns this presentation, not the model. */
  sources?: Array<{ n: number; title?: string; url?: string; domain?: string; publishedAt?: string }>;
  /** Structured news-briefing digest (brew.stories) — when present, the briefing
   *  UI renders in place of the plain markdown answer. */
  storiesDigest?: { answer?: string; stories: Array<{ headline: string; summary: string; why_it_matters?: string; sources: number[]; published_at?: string; category?: string; key_points?: string[]; status?: 'confirmed' | 'developing' }> };
  storiesConfidence?: 'high' | 'medium' | 'low';
  /** When the server synthesised the briefing (epoch ms) — the freshness stamp. */
  storiesTs?: number;
  /** The live play-by-play for an AGENT build (Hermes/Builder) — one entry per
   *  tool the agent runs ("Searching the web", "Writing index.html"), marked done
   *  when its result lands. The bridge streams these as `tool_call`/`tool_result`;
   *  the client used to drop them, so a minute-long build looked like a frozen
   *  spinner. A live trail while working. */
  agentSteps?: Array<{ id: string; label: string; done: boolean }>;
  /** Set at creation for a deliverable build (PDF/DOCX/PPTX/HTML…). Drives the
   *  bottom progress bar. A persistent flag, NOT derived from `progress`, because
   *  later `brew.progress` events overwrite the label mid-build. */
  isBuild?: boolean;
  /** The AGENT'S OWN plan — mirrored live from its `todo` tool calls (each item is
   *  {id, content, status}), NOT guessed from the prompt. Rendered as a high-level
   *  checklist above the tool trail so a long research+build reads as real progress
   *  with the agent's own statuses (pending → in_progress → completed). */
  plan?: Array<{ id: string; label: string; status: 'pending' | 'in_progress' | 'completed' | 'cancelled' }>;
}

/** A file produced by an Agent-mode turn. `path` is what /api/preview resolves
 *  (absolute inside the workspace, or workspace-relative); `name` is the label. */
export interface AgenticFile {
  path: string;
  name: string;
  /** Turn-contract role (Stage 3). Absent when the roles flag is off → all render as
   *  today. When present: 'temporary' is hidden, 'intermediate' collapsed. */
  role?: 'primary' | 'preview' | 'intermediate' | 'temporary';
  artifactType?: string;
}

/** A file the user uploaded into the conversation. Saved in the workspace under
 *  `.uploads/<id>/`; the agent reads/analyzes it with its own tools. */
export interface AttachedFile {
  fileId: string;
  name: string;
  kind: 'doc' | 'image';
  ext?: string;
  mimeType?: string;
  sizeBytes?: number;
  /** Workspace-relative path — what the agent reads and /api/preview serves. */
  path: string;
}

interface Props {
  messages: Msg[];
  streamingAssistantId?: string | null;
  /** Send a follow-up on the user's behalf — wired to the chat composer's send,
   *  so an interactive card (SelectionCard) drives the next turn exactly like a
   *  typed reply. */
  onSelect?: (text: string) => void;
  /** A click on a PRODUCT card's option (Part E, rule #1). Unlike `onSelect`
   *  (which re-sends bare text — used for follow-up chips), this carries the
   *  card's id + owner so the server validates it against the active card and
   *  pins the turn straight back to that product, instead of re-classifying the
   *  option value as a fresh prompt. Wired only when the sidecar is owner-stamped. */
  onCardReply?: (value: string, card: { cardId: string; ownerConnectorId: string }) => void;
  /** Re-send the original question pinned to a connector — wired to the product
   *  disambiguation card so a click routes the turn to the chosen product. */
  onPickProduct?: (originalPrompt: string, connectorId: string) => void;
  /** Edit + re-run the LAST user prompt (only the most recent one is editable,
   *  ChatGPT/Claude-style). Undefined disables the affordance entirely. */
  onEditLast?: (text: string) => void;
  /** Re-run the most recent turn with the same prompt. Only ever wired to the
   *  newest reply — see RegenerateButton. */
  onRegenerate?: (() => void) | undefined;
  /** Open a generated file in the right-side PreviewPane (pdf/docx/xlsx/pptx/...)
   *  — in-platform viewing separate from Download. Wired to Chat's setPreviewFile. */
  onOpenFile?: (path: string, name: string) => void;
}

/**
 * Messages list. DOM uses the legacy .msg / .msg__role / .msg__bubble
 * structure where defined; otherwise lightweight bubbles with role-based
 * alignment.
 *
 * Assistant turns render native ESG cards from `tool_results` (resolveCard)
 * ahead of the markdown reply. The markdown narrative is suppressed when EVERY
 * tool result is rendered by a card (otherwise the card data is duplicated as
 * text). Vision structured-extraction results still render below the body.
 */
export function Messages({ messages, streamingAssistantId, onSelect, onCardReply, onPickProduct, onEditLast, onRegenerate, onOpenFile }: Props) {
  // Index of the LAST user message — only that prompt shows an edit affordance.
  const lastUserIndex = messages.map((m) => m.role).lastIndexOf('user');
  // Which user message is currently being edited (id + working draft), if any.
  const [editing, setEditing] = useState<{ id: string; draft: string } | null>(null);
  // Editing is offered only while nothing is streaming; if a stream starts
  // (e.g. the edit was submitted), drop out of edit mode.
  useEffect(() => {
    if (streamingAssistantId && editing) setEditing(null);
  }, [streamingAssistantId, editing]);
  // resolveCard comes from ESG's remote registry (brew_mfe/cards), loaded once
  // at runtime. Until it loads it yields null → markdown shows, then cards swap
  // in. Brew holds no card source.
  const resolveCard = useRemoteResolveCard();

    // Draw any visual fences the markdown emitted: .mermaid-block (graphs and
    // data charts) and .svg-block (geometric/plotted figures). Both passes share
    // this one debounced tick — a figure is a visual, not a second concern.
    //
    // DEBOUNCED (no dep array): a fence is half-written and unparseable until its
    // closing backticks, so this must re-run as a reply streams — but running the
    // document-wide querySelectorAll on EVERY token adds O(n) main-thread work per
    // token and helped freeze the page on long replies. So we coalesce: each render
    // (re)arms a short timer, and the scan runs once the stream pauses/settles (when
    // fences are actually complete) instead of per token. Each block is marked done
    // once drawn, so it early-returns when there's nothing new.
    const listRef = useRef<HTMLDivElement | null>(null);
    const mermaidTimer = useRef<number | null>(null);
    // GATE the scan on a content signature — a new message arriving, or the
    // streaming reply growing — instead of a bare no-dep-array effect. Previously
    // this re-armed on EVERY render, so a minute-long build (which re-renders on
    // every progress/token event) fired the whole-document querySelectorAll dozens
    // of times a second while the prior turn's SVG/mermaid figures were re-walked
    // each time — the main-thread saturation behind "This page isn't responding".
    // The scan is also scoped to the message list, never document.body, so it never
    // re-walks the lightbox portal or the rest of the app.
    const streamingBodyLen = streamingAssistantId
      ? (messages.find((m) => m.id === streamingAssistantId)?.content.length ?? 0)
      : 0;
    useEffect(() => {
      if (mermaidTimer.current != null) window.clearTimeout(mermaidTimer.current);
      mermaidTimer.current = window.setTimeout(() => {
        const root = listRef.current ?? document.body;
        void renderMermaidIn(root);
        void renderSvgIn(root);
      }, 120);
      return () => { if (mermaidTimer.current != null) window.clearTimeout(mermaidTimer.current); };
    }, [messages.length, streamingAssistantId, streamingBodyLen]);

  // Full-screen lightbox for attached images — click a thumbnail to inspect it.
  const [lightbox, setLightbox] = useState<string | null>(null);
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setLightbox(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox]);

  // One delegated handler for interactive bits inside markdown-rendered HTML
  // (dangerouslySetInnerHTML) — generated images, plus code-block AND list copy
  // buttons. There's no per-block React component to hang an onClick on, so
  // delegation covers streamed blocks and ones reloaded from the DB alike.
  const onBodyClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // A generated image opens full-size in the lightbox (replaces the old
    // "right-click to save" footnote — you can save from the full view).
    const img = (e.target as HTMLElement).closest('img.chat-img') as HTMLImageElement | null;
    if (img) {
      const src = img.currentSrc || img.src;
      if (src) setLightbox(src);
      return;
    }
    // Code-block / list copy button. We read the sibling <code> textContent at
    // click time, so the copied text is always exactly what's on screen.
    const btn = (e.target as HTMLElement).closest('.code-copy, .list-copy') as HTMLButtonElement | null;
    if (!btn) return;
    // .code-copy → the fenced block's <code>; .list-copy → the compact grid's
    // items, one per line (so the copied enumeration pastes as clean text).
    const text = btn.classList.contains('list-copy')
      ? Array.from(btn.parentElement?.querySelectorAll('li') ?? [])
          .map((li) => li.textContent?.trim() ?? '')
          .filter(Boolean)
          .join('\n')
      : btn.parentElement?.querySelector('pre.code code')?.textContent ?? '';
    if (!text || !navigator.clipboard) return;
    // The code button shows text ("Copy"→"Copied"); the list button is an icon
    // (two-page glyph → check), matching the prompt/reply copy controls.
    const isIcon = btn.classList.contains('list-copy');
    navigator.clipboard.writeText(text).then(
      () => {
        btn.classList.add('is-copied');
        if (isIcon) btn.innerHTML = CHECK_ICON;
        else btn.textContent = 'Copied';
        window.setTimeout(() => {
          btn.classList.remove('is-copied');
          if (isIcon) btn.innerHTML = COPY_ICON;
          else btn.textContent = 'Copy';
        }, 1600);
      },
      () => {},
    );
  };

  return (
    <div className="chat-messages" ref={listRef} onClick={onBodyClick}>
      {messages.map((m, i) => {
        const isStreaming = streamingAssistantId === m.id;
        // User: compact right-aligned pill inside the flex row the CSS expects
        // (.msg-row--user justifies it to the end). User text is shown verbatim
        // (React escapes it), never markdown-rendered.
        if (m.role === 'user') {
          const isEditingThis = editing?.id === m.id;
          // Only the last prompt is editable, and only when it's plain text (no
          // attached image), nothing is streaming, and the parent wired a handler.
          const canEdit =
            !!onEditLast && i === lastUserIndex && !streamingAssistantId && !m.imageUrl && !!m.content;
          const submitEdit = () => {
            const next = editing?.draft.trim();
            if (!next) return;
            setEditing(null);
            onEditLast?.(next);
          };
          if (isEditingThis) {
            return (
              <div key={m.id} className="msg-row msg-row--user">
                <div className="msg-edit">
                  <textarea
                    className="msg-edit__input"
                    value={editing!.draft}
                    autoFocus
                    rows={Math.min(8, Math.max(2, editing!.draft.split('\n').length))}
                    onChange={(e) => setEditing({ id: m.id, draft: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitEdit(); }
                      else if (e.key === 'Escape') { e.preventDefault(); setEditing(null); }
                    }}
                  />
                  <div className="msg-edit__actions">
                    <button type="button" className="msg-edit__btn msg-edit__btn--cancel" onClick={() => setEditing(null)}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="msg-edit__btn msg-edit__btn--save"
                      onClick={submitEdit}
                      disabled={!editing!.draft.trim()}
                    >
                      Send
                    </button>
                  </div>
                </div>
              </div>
            );
          }
          return (
            <div key={m.id} className="msg-row msg-row--user">
              <div className={`msg msg--user${m.imageUrl ? ' msg--image' : ''}`}>
                {m.attachments && m.attachments.length > 0 ? (
                  <div className="msg__attachments">
                    {m.attachments.map((a) => (
                      <span key={a.fileId} className="attach-chip" title={a.name}>
                        <span className="attach-chip__icon" aria-hidden="true">{a.kind === 'image' ? '🖼️' : '📄'}</span>
                        <span className="attach-chip__name">{a.name}</span>
                      </span>
                    ))}
                  </div>
                ) : null}
                {m.imageUrl
                  ? <img
                      className="msg__image"
                      src={m.imageUrl}
                      alt={m.content || 'attached image'}
                      onClick={() => setLightbox(m.imageUrl!)}
                    />
                  : m.content ? <div className="msg__body">{m.content}</div> : null}
              </div>
              {/* Reveal-on-hover actions under the pill's bottom-right corner:
                  copy always; edit only on the last prompt. */}
              {m.content || canEdit ? (
                <div className="msg-user-actions">
                  {m.content ? <CopyButton text={m.content} variant="user" title="Copy message" /> : null}
                  {canEdit ? (
                    <button
                      type="button"
                      className="msg-edit-btn"
                      title="Edit message"
                      aria-label="Edit message"
                      onClick={() => setEditing({ id: m.id, draft: m.content })}
                    >
                      <EditGlyph />
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        }
        // Assistant: a vision ACK carries a hidden marker; render a live result
        // card below the body and strip the marker comment.
        const visionTaskId = extractVisionTaskId(m.content);
        const content = visionTaskId
          ? m.content.replace(/<!--\s*brew:vision\s+taskId=[\w-]+\s*-->/, '').trim()
          : m.content;
        // Cards from tool_results — one per supported action. The Brew-native
        // product-pick card (brew_select_product) is rendered by Brew itself;
        // everything else resolves through ESG's remote registry.
        type Renderable =
          | { kind: 'pick'; tr: ToolResultSidecar }
          | { kind: 'deep-search'; tr: ToolResultSidecar }
          | { kind: 'card'; tr: ToolResultSidecar; Card: NonNullable<ReturnType<typeof resolveCard>> };
        const renderable: Renderable[] = (m.toolResults ?? [])
          .map((tr): Renderable | null => {
            if (tr.action === PRODUCT_PICK_ACTION) return { kind: 'pick', tr };
            if (tr.action === DEEP_SEARCH_ACTION) return { kind: 'deep-search', tr };
            const Card = resolveCard(tr.action);
            return Card ? { kind: 'card', tr, Card } : null;
          })
          .filter((x): x is Renderable => x !== null);
        const everyToolRendered =
          (m.toolResults?.length ?? 0) > 0 && renderable.length === m.toolResults!.length;
        const isLast = i === messages.length - 1;
        // Follow-up chips + recommendation are an ESG-ONLY feature — never for
        // Lumara / DataCaffe / Election / Brew Native (now or any future
        // product). Gate on this message's wrapper: only the ESG assistant
        // (brew-esg-1.2) qualifies. For any non-ESG wrapper we skip extraction
        // entirely so a stray "You might also ask:" from another product's model
        // renders as plain text, never as chips.
        const isEsgWrapper = /(^|[^a-z])esg([^a-z]|$)/i.test(m.wrapper || '');
        // Peel the "You might also ask:" list off → clickable chips (ESG only).
        const { body: rawBody, followups } = isEsgWrapper
          ? extractFollowups(content)
          : { body: content, followups: [] };
        // Drop the raw sandbox path — the file is offered as a download card below.
        const mdBody = stripSandboxLinks(rawBody);
        const showMarkdown = !!mdBody && !everyToolRendered;
        const showThinking = !content && renderable.length === 0;
        // Under a card the model narration is hidden — still surface a
        // recommendation: prefer the deterministic one from the tool body,
        // else the model's "Recommendation:" line.
        const cardRecs = everyToolRendered
          ? (m.toolResults ?? []).flatMap((tr) => {
              const b = tr.body as any;
              const arr = b && typeof b === 'object' && Array.isArray(b.recommendations) ? b.recommendations : [];
              return arr.map((r: any) => {
                const action = typeof r?.action === 'string' ? r.action : String(r ?? '');
                let detail: string | undefined;
                let label = 'Why';
                if (r?.expected_reduction && typeof r.expected_reduction === 'object') {
                  detail = `~${r.expected_reduction.low}–${r.expected_reduction.high} ${r.unit ?? ''} (modeled estimate — not a guaranteed reduction)`.trim();
                  label = 'Expected reduction';
                } else if (typeof r?.basis === 'string') {
                  detail = r.basis;
                }
                return { action, detail, label };
              }).filter((r: { action: string }) => r.action);
            })
          : [];
        return (
          <div key={m.id} className="msg msg--assistant">
            {/* "Why this mode" badge — shows what Auto routed to (Pro / Image /
                a persona under Pro), so the routing is visible, never a mystery.
                Fast/Auto turns stay clean. */}
            {(() => {
              const badge = modeBadgeFor(m.wrapper);
              return badge ? (
                <div className="mode-badge" title="Breww picked this mode for you">
                  <span aria-hidden="true">{badge.icon}</span>
                  <span className="mode-badge__label">Breww {badge.label}</span>
                </div>
              ) : null;
            })()}
            {/* The raw model id is deliberately NOT shown to users — an internal
                infra detail (e.g. "di-deepseek-v4-pro") leaks the provider and reads
                as unprofessional. The product-facing mode badge above is enough. */}
            {/* No role header: the model name is never shown, and the single
                Breww mark lives in the actions row below (animated while the
                reply streams, static beside the copy button when done). */}
            {/* Rich cards first — one per supported tool result. Each card is
                isolated so a bad payload degrades to markdown, not a crash. Only
                the latest assistant turn is interactive — a click on a
                scrolled-up historical picker would send a stale choice. */}
            {renderable.map((r, idx) =>
              r.kind === 'pick' ? (
                <CardBoundary key={`pick-${idx}`}>
                  <ProductPickCard
                    body={r.tr.body}
                    {...(isLast && onPickProduct ? { onPickProduct } : {})}
                  />
                </CardBoundary>
              ) : r.kind === 'deep-search' ? (
                <CardBoundary key={`deep-search-${idx}`}>
                  <DeepSearchCard body={r.tr.body} />
                </CardBoundary>
              ) : (
                <CardBoundary key={`${r.tr.action}-${idx}`}>
                  <r.Card
                    body={r.tr.body}
                    args={r.tr.args}
                    status={r.tr.status}
                    {...(isLast
                      // Owner-stamped card → route the option click as a CardReply
                      // (owner-bound). Otherwise fall back to a bare text re-send.
                      ? (r.tr.cardId && r.tr.ownerConnectorId && onCardReply
                          ? { onSelect: (value: string) => onCardReply(value, { cardId: r.tr.cardId as string, ownerConnectorId: r.tr.ownerConnectorId as string }) }
                          : onSelect ? { onSelect } : {})
                      : {})}
                  />
                </CardBoundary>
              ),
            )}
            {/* Recommendation under a card (card hides the narration). Prefer the
                deterministic body one; else the model's "Recommendation:" line. */}
            {isEsgWrapper && everyToolRendered && (cardRecs.length > 0 || extractRecommendation(content)) ? (
              <div className="msg__body" style={{ marginTop: 8 }}>
                {cardRecs.length > 0
                  ? cardRecs.map((r, idx) => (
                      <div key={idx} style={{ marginBottom: 4 }}>
                        <div><strong>Recommendation:</strong></div>
                        <div style={{ marginLeft: 12 }}><strong>Action:</strong> {r.action}</div>
                        {r.detail ? <div style={{ marginLeft: 12 }}><strong>{r.label}:</strong> {r.detail}</div> : null}
                      </div>
                    ))
                  : <div><strong>Recommendation:</strong> {extractRecommendation(content)}</div>}
              </div>
            ) : null}
            {/* Reasoning-model chain-of-thought: live while the model thinks,
                collapses to "Thought for Ns" the moment the answer begins. */}
            {m.thinking ? (
              <ThinkingTrail text={m.thinking} sec={m.thinkingSec} />
            ) : null}
            {/* The live agent build trail: each tool the agent runs, shown as it
                happens instead of a silent spinner. The last step pulses while
                working; once the answer lands it reads as a done checklist. */}
            {/* The agent's OWN plan (from its `todo` tool), mirrored live with its
                real statuses — a high-level "here's what I'm doing" checklist above
                the tool trail. Stays while streaming; after the turn it lingers ONLY
                if something is still unfinished (a partial-completion signal). */}
            {m.plan && m.plan.length > 0
              && (isStreaming || m.plan.some((d) => d.status !== 'completed' && d.status !== 'cancelled')) ? (
              <div className="agent-trail agent-trail--plan" aria-live="polite">
                {m.plan.map((d) => {
                  const done = d.status === 'completed';
                  const active = d.status === 'in_progress';
                  const cancelled = d.status === 'cancelled';
                  return (
                    <div key={d.id} className={`agent-trail__step ${done ? 'is-done' : active ? 'is-active' : ''}`}
                         style={{ ...(!done && !active ? { opacity: 0.55 } : {}), ...(cancelled ? { textDecoration: 'line-through' } : {}) }}>
                      <span className="agent-trail__dot" aria-hidden="true">
                        {done ? (
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                            <path d="M20 6 9 17l-5-5" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        ) : active ? <span className="agent-trail__pulse" /> : <span aria-hidden="true">·</span>}
                      </span>
                      <span className="agent-trail__label">{d.label}</span>
                    </div>
                  );
                })}
              </div>
            ) : null}
            {/* ONE compact execution panel, above the answer, updating in place.
                Only WHILE building — once the turn is done the answer + file card
                are the result, and a lingering "Writing report.pdf" would read as
                still-writing. It absorbs both the old step trail (which grew a row
                per step and shoved the answer down mid-read) and the separate
                build bar, so execution occupies one stable block instead of two
                that resize. */}
            {isStreaming && ((m.agentSteps && m.agentSteps.length > 0) || m.isBuild) ? (
              <ExecutionPanel
                steps={m.agentSteps ?? []}
                progress={m.progress}
                isBuild={m.isBuild}
              />
            ) : null}
            {m.error ? (
              // A failed generation, reloaded. Rendered with the same .chat-error
              // treatment as the live banner so reopening a chat shows exactly what
              // the user saw at the time — rather than the error text arriving as
              // plain prose, which would read as the assistant having SAID it.
              <div className="chat-error">{content}</div>
            ) : m.storiesDigest ? (
              // Structured news briefing renders in place of the plain markdown.
              <StoriesBriefing
                digest={m.storiesDigest}
                sources={m.sources ?? []}
                {...(m.storiesConfidence ? { confidence: m.storiesConfidence } : {})}
                {...(m.storiesTs ? { updatedAt: m.storiesTs } : {})}
              />
            ) : showMarkdown ? (
              <>
                <MessageBody body={mdBody} streaming={isStreaming} />
                {/* Outline for a long answer — LAST assistant turn only.
                    Mounting it per message was an O(N) defect: each rail creates a
                    ResizeObserver on the SHARED .chat scroller plus an
                    IntersectionObserver over its headings, so a 40-message chat
                    carried ~40 observers watching hundreds of elements. Every
                    scroll fired all of them and every one called setState; every
                    resize forced a synchronous layout per rail. The page got
                    slower the longer you talked, which is the worst possible
                    shape for this bug — it looks fine in testing and dies in real
                    use. Navigation only matters for the answer you are reading;
                    scrolled-away turns never needed an outline. */}
                {isLast ? <SectionRail body={mdBody} streaming={isStreaming} /> : null}
              </>
            ) : showThinking ? (
              <div className="msg__body">
                {isStreaming
                  ? m.progressPhase === 'image'
                    ? <ImageGenStatus label={m.progress} />
                    // Animated brew loop + rotating verb while the reply is
                    // being generated; it stops the instant content lands and
                    // this branch unmounts.
                    : (m.agentSteps && m.agentSteps.length > 0)
                      ? null
                      : <span className="brew-thinking-row"><BrewLoader /><ThinkingStatus label={m.progress} /></span>
                  : null}
              </div>
            ) : null}
            {/* Web-search source cards (Perplexity pattern). Hidden when the full
                briefing renders — it already includes the source cards. */}
            {m.sources && m.sources.length > 0 && !m.storiesDigest ? <SourceCards sources={m.sources} /> : null}
            {/* Agent-mode downloads — files the agent actually created this turn.
                Each opens/saves via the workspace preview endpoint. */}
            {(m.files ?? []).some((f) => !isSpecArtifact(f.name) && f.role !== 'temporary') ? (
              <div className="brew-files">
                {m.files!.filter((f) => !isSpecArtifact(f.name) && f.role !== 'temporary').map((f) => {
                  const meta = fileMeta(f.name);
                  return (
                    <div key={f.path} className="brew-file-item">
                    <div
                      className="brew-file-card"
                      style={{ '--file-accent': meta.accent } as CSSProperties}
                    >
                      <span className="brew-file-card__icon" aria-hidden="true">
                        <FileGlyph kind={meta.kind} />
                      </span>
                      <span className="brew-file-card__meta">
                        <span className="brew-file-card__name">{f.name}</span>
                        <span className="brew-file-card__type">{meta.category} · {meta.ext.toUpperCase()}</span>
                      </span>
                      {/* View opens the file in the split preview pane (right side) — no
                          download needed. HTML is excluded (it has its own inline toggle below). */}
                      {onOpenFile && canViewInPane(f.name) ? (
                        <button
                          type="button"
                          className="brew-file-card__dl brew-file-card__view"
                          onClick={() => onOpenFile(f.path, f.name)}
                          title={`View ${f.name}`}
                        >
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
                            <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.9" />
                          </svg>
                          View
                        </button>
                      ) : null}
                      {/* Only the button triggers a download — clicking the name/card does nothing.
                          A capability is minted on click (JWT stays in the auth header, never the URL). */}
                      <button
                        type="button"
                        className="brew-file-card__dl"
                        onClick={() => { void downloadFile(f.path, f.name); }}
                        title={`Download ${f.name}`}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <path d="M12 3v11m0 0l-4.5-4.5M12 14l4.5-4.5M4 20h16" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Download
                      </button>
                    </div>
                    {/* Refine-in-place dock (Breww Pro brief §4): quick chips that
                        iterate the SAME file via the sticky-builder path. */}
                    {isLast && onSelect && !isStreaming ? (
                      <div className="refine-dock">
                        {refineChipsFor(f.name).map((c, ci) => (
                          <button
                            key={ci}
                            type="button"
                            className="refine-chip"
                            onClick={() => onSelect(c.prompt)}
                            title={c.prompt}
                          >
                            {c.label}
                          </button>
                        ))}
                      </div>
                    ) : null}
                    </div>
                  );
                })}
              </div>
            ) : null}
            {/* Clickable follow-up chips — one click sends the question. */}
            {isEsgWrapper && followups.length > 0 && onSelect && !isStreaming ? (
              <div className="brew-followups" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6, marginTop: 10 }}>
                {followups.map((q, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className="brew-followup-chip"
                    title="Ask this"
                    onClick={() => onSelect(q)}
                    style={{ cursor: 'pointer', textAlign: 'left', maxWidth: '100%', padding: '6px 12px', borderRadius: 16, border: '1px solid rgba(120,120,120,0.35)', background: 'rgba(127,127,127,0.08)', color: 'inherit', font: 'inherit', fontSize: '0.9em', lineHeight: 1.3 }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(127,127,127,0.20)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(127,127,127,0.08)'; }}
                  >
                    {q}
                  </button>
                ))}
              </div>
            ) : null}
            {/* Bottom row: the Breww mark animates (bottom→top reveal) WHILE the
                reply streams, then leaves — this row is a toolbar, and a static
                logo sitting in it reads as a button that does nothing. Once the
                turn is done only real actions remain. During the pre-content
                wait the thinking indicator above carries the mark instead, so
                exactly one logo is ever on screen. */}
            {content ? (
              <div className="msg__reply-actions">
                {isStreaming ? <BrewLoader /> : (
                  <>
                    <CopyButton text={content} variant="assistant" title="Copy response" />
                    <PlayVoiceButton text={content} />
                    {isLast && onRegenerate ? (
                      <RegenerateButton onClick={onRegenerate} title="Regenerate response" />
                    ) : null}
                  </>
                )}
              </div>
            ) : null}
            {visionTaskId ? <VisionResult taskId={visionTaskId} /> : null}
          </div>
        );
      })}
      {lightbox && createPortal(
        // Portaled to <body> so it escapes the chat's stacking context and truly
        // covers everything (the composer used to paint on top of it). Backdrop /
        // ✕ / Esc close; the toolbar offers Download + Open in a new tab.
        <div
          className="img-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Image preview"
          onClick={() => setLightbox(null)}
        >
          <div className="img-lightbox__bar" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="img-lightbox__action"
              title="Download image"
              onClick={() => void downloadImageSrc(lightbox)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M12 3v11m0 0l-4.5-4.5M12 14l4.5-4.5M4 20h16" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>Download</span>
            </button>
            <a
              className="img-lightbox__action"
              href={lightbox}
              target="_blank"
              rel="noopener noreferrer"
              title="Open in a new tab"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M14 4h6v6M20 4l-8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M18 13v5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>Open</span>
            </a>
            <button
              type="button"
              className="img-lightbox__action img-lightbox__action--icon"
              aria-label="Close image preview"
              onClick={() => setLightbox(null)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>
          <img
            className="img-lightbox__img"
            src={lightbox}
            alt="Generated image"
            onClick={(e) => e.stopPropagation()}
          />
        </div>,
        document.body,
      )}
    </div>
  );
}
